import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import {
  Receipt, ScanLine, TrendingDown, Users, Store, Loader2, Crown,
} from 'lucide-react';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 days' },
  { key: 'month', label: '30 days' },
  { key: 'year', label: '1 year' },
];

const formatPHP = (n) =>
  `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * AdminTransactionsPage — cross-merchant aggregated view for admins.
 * Mirrors the merchant dashboard layout but spans the whole platform.
 */
export function AdminTransactionsPage() {
  const { api } = useAuth();
  const [range, setRange] = useState('week');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/scan/stats?range=${range}`);
      setStats(data);
    } catch (e) {
      console.error('Failed to load admin scan stats:', e);
    }
    setLoading(false);
  }, [api, range]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminSidebarLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-white">Member Redemption Analytics</h1>
          <p className="text-[#A0B4CC] text-sm mt-1">Platform-wide view of every member QR scan, sale, and discount given.</p>
        </div>

        {/* Range selector */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="admin-range-selector">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                range === r.key
                  ? 'bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40'
                  : 'bg-[#041A3B] text-[#A0B4CC] border-white/10 hover:text-white'
              }`}
              data-testid={`admin-range-${r.key}-btn`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loading && !stats ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Total scans"          value={stats?.total_scans || 0}                       icon={ScanLine}     accent="#00E5FF" testId="admin-kpi-scans" />
              <Kpi label="Total sales revenue"  value={formatPHP(stats?.total_revenue || 0)}          icon={Receipt}      accent="#00E676" testId="admin-kpi-revenue" hint={`${stats?.sales_count || 0} sales`} />
              <Kpi label="Total discounts"      value={formatPHP(stats?.total_discount_given || 0)}   icon={TrendingDown} accent="#FFD740" testId="admin-kpi-discounts" />
              <Kpi label="Active merchants"     value={stats?.active_merchants || 0}                  icon={Store}        accent="#7C4DFF" testId="admin-kpi-merchants" hint={`${stats?.active_members || 0} members`} />
            </div>

            {stats?.top_merchants?.length > 0 && (
              <Card className="bg-[#041A3B] border-white/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className="w-4 h-4 text-[#FFD740]" />
                    <h2 className="font-outfit text-lg text-white">Top merchants by scan volume</h2>
                  </div>
                  <div className="divide-y divide-white/5" data-testid="admin-top-merchants-list">
                    {stats.top_merchants.map((m, i) => (
                      <div key={m.merchant_id} className="py-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#000B18] border border-white/10 flex items-center justify-center text-[#00E5FF] text-xs font-semibold shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{m.merchant_business_name}</p>
                          <p className="text-[#5C7599] text-xs">{m.scans} scan{m.scans === 1 ? '' : 's'}</p>
                        </div>
                        <span className="text-[#00E676] text-sm font-mono shrink-0">{formatPHP(m.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {(!stats || stats.total_scans === 0) && (
              <Card className="bg-[#041A3B] border-white/10">
                <CardContent className="p-10 text-center">
                  <Users className="w-10 h-10 text-[#5C7599] mx-auto mb-3 opacity-50" />
                  <p className="text-[#A0B4CC] text-sm">
                    No scans recorded in this period yet. Encourage your merchants to start scanning member QRs to populate this view.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminSidebarLayout>
  );
}

function Kpi({ label, value, icon: Icon, accent, hint, testId }) {
  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-[#A0B4CC] text-xs uppercase tracking-wider">{label}</p>
            <p className="font-outfit text-2xl mt-1 truncate" style={{ color: accent }}>{value}</p>
            {hint && <p className="text-[#5C7599] text-[11px] mt-1">{hint}</p>}
          </div>
          <Icon className="w-5 h-5 shrink-0 opacity-60" style={{ color: accent }} />
        </div>
      </CardContent>
    </Card>
  );
}

export default AdminTransactionsPage;
